import { useEffect, useRef, type ReactNode } from 'react'

interface Props {
  open: boolean
  /** Named on the dialog itself, so a screen reader says what is being edited. */
  title: string
  subtitle?: ReactNode
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * A side sheet for editing one item out of a list.
 *
 * The list screens used to render every item as a full editor card, which meant the
 * page's height grew with the number of things configured and the *reading* task —
 * "what is being tracked here" — was buried under the *editing* task. Rows answer the
 * first; this answers the second, one item at a time.
 *
 * `onClose` is held in a ref rather than named as an effect dependency. Callers pass a
 * closure over their own state, so it is a new function on every render, and depending
 * on it would re-run the effect on every keystroke — stealing focus back to the panel
 * from whichever input the user was typing into.
 */
export function Drawer({ open, title, subtitle, onClose, footer, children }: Props) {
  const panel = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)

  // Declared before the effect below so it has run by the time a key is handled.
  useEffect(() => {
    close.current = onClose
  })

  useEffect(() => {
    if (!open) return

    const restore = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panel.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        close.current()
        return
      }
      if (event.key !== 'Tab' || !panel.current) return
      // Tab must not walk out into the page behind, which is inert but still focusable.
      const targets = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (targets.length === 0) return
      const first = targets[0]
      const last = targets[targets.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      restore?.focus?.()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="drawer"
      role="presentation"
      // mousedown rather than click: a drag that starts inside the panel and ends on the
      // backdrop is a text selection, not a request to discard the edit.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <header className="drawer__head">
          <div className="drawer__heading">
            <h3 className="drawer__title">{title}</h3>
            {subtitle && <p className="drawer__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>
        <div className="drawer__body">{children}</div>
        {footer && <footer className="drawer__footer">{footer}</footer>}
      </div>
    </div>
  )
}
